import { motion, AnimatePresence, type HTMLMotionProps } from "motion/react";
import { css } from "@emotion/react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import React, { useEffect, useState } from "react";
import WarningIcon from "@mui/icons-material/Warning";
import { useTheme } from "../theme";
import { copyToClipboard } from "../@utils/copyToClipboard";
import { Tooltip } from "../Tooltip";
import { Button, resetButtonStyle } from "../Button";
import { useAlert } from "./AlertContext";

export type AlertDuration = number | "standard" | "short";

export interface AlertOptions {
  /**
   * uniqueな文字列を入れてください。removeAlertを用いて任意のAlertを削除する場合に使用できます。
   * 指定しない場合に任意の文字列が生成され、addAlertから取得できます。
   */
  id?: string;
  /**
   * 役割を表し、左側のアイコンと色を変更します。
   * @default "success"
   */
  severity?: "error" | "success" | "warning";
  /**
   * トーストコンポーネントが消えるまでの時間(ms)を表します。
   * @default Infinity
   */
  duration?: AlertDuration;
}

export interface AlertRichMessageType {
  reason?: string;
  action?: string;
  happend: string;
  log?: string;
}

export type AlertMessageType = string | AlertRichMessageType;

export interface AlertProps
  extends
    AlertOptions,
    Omit<
      HTMLMotionProps<"div">,
      | "id"
      | "onAnimationComplete"
      | "onDrag"
      | "onDragStart"
      | "onDragEnd"
      | "onAnimationStart"
    > {
  message: AlertMessageType;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      message,
      duration = Infinity,
      id,
      severity = "success",
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const { removeAlert } = useAlert();
    const [show, setShow] = useState(true);
    const Icon =
      severity === "success"
        ? CheckIcon
        : severity === "warning"
          ? WarningIcon
          : ErrorIcon;

    const theme = useTheme();

    useEffect(() => {
      if (show && duration !== Infinity) {
        const timeout = setTimeout(
          () => {
            setShow(false);
          },
          duration === "standard"
            ? 3500
            : duration === "short"
              ? 1500
              : duration,
        );

        return () => clearTimeout(timeout);
      }
      return undefined;
    }, [duration, id, removeAlert, show]);

    return (
      <AnimatePresence>
        {show && (
          <motion.div
            ref={ref}
            key={id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onAnimationComplete={(def: { opacity: number }) => {
              if (def.opacity === 0) {
                if (id) {
                  removeAlert(id);
                }
              }
            }}
            css={css`
              z-index: ${theme.zIndex.tooltip};
              display: grid;
              grid-template-columns: auto 1fr auto;
              width: fit-content;
              max-width: 384px;
              min-height: 56px;
              overflow: hidden;
              color: ${theme.palette.intentions[severity].contrastText};
              background-color: ${theme.palette.intentions[severity].main};
              border-radius: 4px;
              box-shadow: ${theme.shadows[4]};
            `}
            role="alert"
            className={className}
            style={style}
            {...props}
          >
            <div
              css={css`
                padding: ${theme.spacing}px;
                background-color: ${theme.palette.intentions[severity].dark};
              `}
            >
              <Icon fontSize="medium" />
            </div>
            <div
              css={[
                css`
                  display: flex;
                  flex-direction: column;
                  gap: ${theme.spacing}px;
                  padding: ${theme.spacing}px ${theme.spacing * 2}px
                    ${theme.spacing * 2}px;
                `,
              ]}
            >
              <div
                css={[
                  theme.typography.body,
                  css`
                    word-break: break-all;
                  `,
                ]}
              >
                {typeof message === "string" ? message : message.happend}
              </div>
              {typeof message === "string" ? null : (
                <>
                  <div css={[theme.typography.caption]}>
                    {message.reason}
                    {message.action ? (
                      <span
                        css={css`
                          font-weight: bolder;
                        `}
                      >
                        {message.action}
                      </span>
                    ) : null}
                  </div>
                  {message.log ? (
                    <div
                      css={css`
                        text-align: right;
                      `}
                    >
                      <CopyButton value={message.log}>
                        エラーログをコピーする
                      </CopyButton>
                    </div>
                  ) : null}
                </>
              )}
            </div>
            <div>
              <Button
                css={css`
                  color: ${theme.palette.intentions[severity].contrastText};
                `}
                icon={<CloseIcon fontSize="small" />}
                rounded
                onClick={() => {
                  setShow(false);
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
);
Alert.displayName = "Alert";

// Todo: Use TextButton
const CopyButton = ({
  children,
  value,
}: {
  children: string;
  value: string;
}) => {
  const [copyMessage, setCopyMessage] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);

  const theme = useTheme();
  return (
    <Tooltip title={copyMessage} arrow placement="top-end" open={copied}>
      <button
        css={[
          resetButtonStyle,
          theme.typography.caption,
          css`
            text-decoration: underline;
          `,
        ]}
        onClick={() => {
          const flag = copyToClipboard(value);
          setCopyMessage(flag ? "コピーしました" : "コピーできませんでした");
          setCopied(true);
          setTimeout(
            () => {
              setCopied(false);
            },
            flag ? 2500 : 100000,
          );
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
};
